import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { reportError } from '~shared/observability/report-error';

// Axios errors are already captured by the response interceptor in base-api.ts,
// so we skip them here to avoid duplicate Sentry issues. This handler exists to
// catch non-HTTP failures thrown inside query/mutation functions.
const isHandledElsewhere = (error: unknown) => axios.isAxiosError(error);

/**
 * ENR-2522: default cache so remounts/navigation do not refetch everything.
 * Hooks that need live data keep an explicit `staleTime: 0` override.
 * Mutations still invalidate the right keys after writes.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (isHandledElsewhere(error)) return;
      reportError(error, {
        featureArea: 'query',
        queryKey: query.queryKey,
      });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (isHandledElsewhere(error)) return;
      reportError(error, {
        featureArea: 'mutation',
        mutationKey: mutation.options.mutationKey,
      });
    },
  }),
});
